// pages/vehicles/VehicleFormPage.tsx
import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { fieldValue } from '../../app/components/polaris';
import Combobox from '../../app/components/Combobox';
// import {useNavigate} from 'react-router-dom';

type FieldErrors = {
    yearFrom?: string;
    yearTo?: string;
    make?: string;
    model?: string;
};

const YEAR_MIN = 1886;
const YEAR_MAX = 2100;

const toOptions = (values: string[]) => values.map(value => ({ label: value, value }));

const VehicleFormPage: React.FC = () => {
    // const navigate = useNavigate();

    // ---- Suggestions (replace with data from store/API as needed) ----
    const makeSuggestions = useMemo(
        () => toOptions(['Toyota', 'Honda', 'Ford', 'Chevrolet', 'BMW', 'Mercedes-Benz', 'Volkswagen', 'Nissan', 'Hyundai', 'Kia']),
        [],
    );
    const modelSuggestions = useMemo(
        () => toOptions(['Camry', 'Civic', 'F-150', 'Silverado', '3 Series', 'C-Class', 'Golf', 'Altima', 'Elantra', 'Sorento']),
        [],
    );
    const submodelSuggestions = useMemo(
        () => toOptions(['Base', 'SE', 'LE', 'XLE', 'Sport', 'Limited', 'Touring', 'Premium']),
        [],
    );

    // ---- Form state ----
    const [yearFrom, setYearFrom] = useState('');
    const [yearTo, setYearTo] = useState('');
    const [make, setMake] = useState('');
    const [model, setModel] = useState('');
    const [submodel, setSubmodel] = useState('');

    // ---- Errors ----
    const [errors, setErrors] = useState<FieldErrors>({});
    const [submitError, setSubmitError] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState(false);

    // ---- Helpers ----
    const clampDigits = (val: string) => val.replace(/[^\d]/g, '').slice(0, 4);

    const validate = useCallback((): boolean => {
        const e: FieldErrors = {};

        // Make & Model required
        if (!make.trim()) e.make = 'Make is required';
        if (!model.trim()) e.model = 'Model is required';

        // Years optional, but must be valid if present
        const yf = yearFrom ? parseInt(yearFrom, 10) : undefined;
        const yt = yearTo ? parseInt(yearTo, 10) : undefined;

        const inRange = (y: number) => y >= YEAR_MIN && y <= YEAR_MAX;

        if (yearFrom && (isNaN(yf!) || !inRange(yf!))) {
            e.yearFrom = `Enter a valid year between ${YEAR_MIN}-${YEAR_MAX}`;
        }
        if (yearTo && (isNaN(yt!) || !inRange(yt!))) {
            e.yearTo = `Enter a valid year between ${YEAR_MIN}-${YEAR_MAX}`;
        }
        if (!e.yearFrom && !e.yearTo && yf !== undefined && yt !== undefined && yf > yt) {
            e.yearTo = 'End year must be greater than or equal to start year';
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    }, [make, model, yearFrom, yearTo]);

    // ---- Submit ----
    const onSave = useCallback(async () => {
        setSubmitError(undefined);
        if (!validate()) return;

        setSubmitting(true);
        try {
            // TODO: wire to your store
            // navigate('/apps/partfinder-development/vehicles'); // or your route
        } catch (err: any) {
            setSubmitError(err?.message ?? 'Failed to create vehicle');
        } finally {
            setSubmitting(false);
        }
    }, [validate]);

    const onCancel = useCallback(() => {
        // navigate back
    }, []);

    return (
        <s-page heading="Add vehicle" inlineSize="small">
            <s-link slot="breadcrumb-actions" onClick={onCancel}>Vehicles</s-link>
            <s-button slot="primary-action" variant="primary" loading={submitting} onClick={onSave}>Save</s-button>
            <s-button slot="secondary-actions" disabled={submitting} onClick={onCancel}>Cancel</s-button>
            <s-stack gap="base">
                <s-heading fontSize="large-200">Add vehicle</s-heading>
                {submitError && <s-banner tone="critical">{submitError}</s-banner>}
                <s-section heading="Vehicle details">
                    <form onSubmit={e => { e.preventDefault(); onSave(); }}>
                        <s-stack gap="base">
                            <s-grid gridTemplateColumns="1fr 1fr" gap="base">
                                <s-text-field
                                    label="Year (from)"
                                    value={yearFrom}
                                    placeholder="e.g. 2010"
                                    error={errors.yearFrom}
                                    autocomplete="off"
                                    onInput={event => setYearFrom(clampDigits(fieldValue(event)))}
                                />
                                <s-text-field
                                    label="Year (to)"
                                    value={yearTo}
                                    placeholder="e.g. 2018"
                                    error={errors.yearTo}
                                    autocomplete="off"
                                    onInput={event => setYearTo(clampDigits(fieldValue(event)))}
                                />
                            </s-grid>
                            <Combobox label="Make" value={make} options={makeSuggestions} error={errors.make}
                                placeholder="e.g. Toyota" onChange={setMake} />
                            <Combobox label="Model" value={model} options={modelSuggestions} error={errors.model}
                                placeholder="e.g. Camry" onChange={setModel} />
                            <Combobox label="Submodel (optional)" value={submodel} options={submodelSuggestions}
                                placeholder="e.g. SE" onChange={setSubmodel} />
                        </s-stack>
                    </form>
                </s-section>
            </s-stack>
        </s-page>
    );
};

export default observer(VehicleFormPage);
